import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useApp } from '@/presentation/hooks/use-app.js';
import { formatDate } from '@/shared/utils/format-date.js';
import type { Complexity } from '@/shared/utils/format-status.js';

interface Props {
    complexity: Complexity | null;
    estimatedHours: number | null;
    /** Coluna 3 — exclusiva da caixa da IA. `undefined` omite a coluna; `null` exibe o placeholder. */
    aiScore?: number | null;
    /** Coluna 4 — exclusiva da caixa da Fábrica de Software. `undefined` omite a coluna; `null` exibe o placeholder. */
    estimatedDeliveryDate?: string | null;
    labelColor?: string;
}

// Largura fixa por coluna: mantém Consultor Funcional, IA e Fábrica de Software alinhadas
// verticalmente entre si mesmo tendo quantidades diferentes de colunas. Exportada para que os
// campos em modo de edição ocupem exatamente a mesma grade dos campos em modo de leitura.
export const ESTIMATE_COLUMN_WIDTH = 200;

const PLACEHOLDER = '—';

/**
 * Layout padrão das caixas de estimativa (Consultor Funcional, IA e Fábrica de Software).
 * A ordem e os rótulos das colunas ficam aqui — e não em cada chamador — para que as caixas
 * não voltem a divergir entre si.
 */
export function EstimateSummary({ complexity, estimatedHours, aiScore, estimatedDeliveryDate, labelColor = 'text.secondary' }: Props) {
    const { translate } = useApp();

    const columns: Array<{ key: string; label: string; value: string }> = [
        { key: 'complexity', label: translate('estimate.complexity'), value: complexity ? translate(`complexityLabels.${complexity}`) : PLACEHOLDER },
        { key: 'estimatedHours', label: translate('estimate.estimatedHours'), value: estimatedHours !== null ? `${estimatedHours}h` : PLACEHOLDER }
    ];

    if (aiScore !== undefined) {
        columns.push({ key: 'aiScore', label: translate('estimate.aiScoreLabel'), value: aiScore !== null ? String(aiScore) : PLACEHOLDER });
    }

    if (estimatedDeliveryDate !== undefined) {
        columns.push({ key: 'estimatedDelivery', label: translate('estimate.estimatedDelivery'), value: formatDate(estimatedDeliveryDate) });
    }

    return (
        <Stack direction="row" flexWrap="wrap" rowGap={1.5} data-testid="estimate-summary">
            {columns.map((column) => (
                <Box key={column.key} data-testid={`estimate-column-${column.key}`} sx={{ width: ESTIMATE_COLUMN_WIDTH, flexShrink: 0, pr: 2 }}>
                    <Typography variant="caption" color={labelColor} sx={{ display: 'block' }}>
                        {column.label}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                        {column.value}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
}
